import type {
  TaskPage,
  TaskPinBoardPage,
  TaskPinBoardWrite,
  TaskWrite,
  TaskWriteResult,
} from "../web-client/api/data-contracts";
import * as operations from "../web-client/api/operations";
import type { Transport } from "../web-client/transport";

/**
 * One page request against a task log.
 *
 * A walk's first request may send `since`, or nothing, and the server floors it at the lookback; every later request
 * sends only the `cursor` the previous answer carried. `limit` caps the rows on the page.
 */
export type TaskLogQuery = { workspaceId: string; since?: string; cursor?: string; limit: number };

/**
 * One page of the workspace's tasks, oldest first.
 *
 * A cursor the server refuses answers 400, and the caller drops it and cold-starts.
 */
export function listTasks(transport: Transport, query: TaskLogQuery): Promise<TaskPage> {
  return operations.getWorkspacesTasks(transport, query);
}

/**
 * One page of the workspace's task pins, oldest first.
 *
 * A cursor the server refuses answers 400, and the caller drops it and cold-starts.
 */
export function listTaskPinBoards(transport: Transport, query: TaskLogQuery): Promise<TaskPinBoardPage> {
  return operations.getWorkspacesTasksPinBoards(transport, query);
}

/**
 * Records a batch of tasks, each with its subjects and its first pin.
 *
 * Idempotent on each write's `id`, so a retry is safe.
 */
export function writeTasks(
  transport: Transport,
  { workspaceId, tasks }: { workspaceId: string; tasks: TaskWrite[] },
): Promise<TaskWriteResult> {
  return operations.postWorkspacesTasks(transport, { workspaceId, body: { tasks } });
}

/**
 * Records a batch of pins moving tasks between boards.
 *
 * Idempotent on each pin's `id`, so a retry is safe.
 */
export function writeTaskPinBoards(
  transport: Transport,
  { workspaceId, pinBoards }: { workspaceId: string; pinBoards: TaskPinBoardWrite[] },
): Promise<TaskWriteResult> {
  return operations.postWorkspacesTasksPinBoards(transport, { workspaceId, body: { pinBoards } });
}
